import { useMarkets } from '@/state/markets';
import { useWallet } from '@/state/wallet';
import { useLife } from '@/state/life';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { useMeta } from '@/state/meta';
import { useNotifications } from '@/state/notifications';
import { usePhone } from '@/state/phone';
import { sounds } from '@/audio/synth';
import { getTicker } from '@/content/tickers';
import { priceAt } from './prices';

export interface Achievement {
  id: string;
  label: string;
  description: string;
  predicate: () => boolean;
}

export const ACHIEVEMENTS: Achievement[] = [
  {
    id: 'first-blood',
    label: 'First Blood',
    description: 'Filled your first order.',
    predicate: () => useMarkets.getState().recentFills.length > 0,
  },
  {
    id: 'first-l',
    label: "First L",
    description: 'Realized your first loss. The first of many.',
    predicate: () => useMarkets.getState().realizedPnl < 0,
  },
  {
    id: 'guh',
    label: 'GUH',
    description: 'Lost more than $1,000 on a single position.',
    predicate: () => useMarkets.getState().recentFills.some((f) => f.side === 'sell' && false) || useMarkets.getState().realizedPnl < -1000,
  },
  {
    id: 'sell-dog',
    label: 'Sell The Dog',
    description: 'Sold the dog.',
    predicate: () => !useWallet.getState().possessions.find((p) => p.id === 'dog')?.available,
  },
  {
    id: 'sell-house',
    label: 'Sold The House',
    description: 'Liquidated your only home. Office floor next.',
    predicate: () => !useWallet.getState().possessions.find((p) => p.id === 'house')?.available,
  },
  {
    id: 'wife-divorce-filed',
    label: 'Filed',
    description: 'Marriage rep at -60. Lawyer is involved.',
    predicate: () => useLife.getState().relationships.wife <= -60,
  },
  {
    id: 'irs-audit',
    label: 'Audited',
    description: 'IRS heat reached 50.',
    predicate: () => useLife.getState().irsHeat >= 50,
  },
  {
    id: 'sanity-zero',
    label: "I'm Fine",
    description: 'Sanity hit 0. You are not fine.',
    predicate: () => useLife.getState().sanity <= 0,
  },
  {
    id: 'first-tweet',
    label: 'First Post',
    description: 'Posted on Y for the first time.',
    predicate: () => useXClone.getState().posts.some((p) => p.authorIsSelf),
  },
  {
    id: 'cult-following-100',
    label: 'Cult Following',
    description: 'Reached 100 Y followers.',
    predicate: () => useXClone.getState().followers >= 100,
  },
  {
    id: 'whale',
    label: 'Whale',
    description: 'Net worth crossed $100K. You\'ve graduated to Whale tier.',
    predicate: () => useMeta.getState().highestTierRankReached >= 2,
  },
  {
    id: 'tycoon',
    label: 'Tycoon',
    description: 'Net worth crossed $10M.',
    predicate: () => useMeta.getState().highestTierRankReached >= 3,
  },
  {
    id: 'billionaire',
    label: 'Billionaire',
    description: 'You did it. The world is your unethical, deeply tax-advantaged oyster.',
    predicate: () => useMeta.getState().highestTierRankReached >= 4,
  },
  {
    id: 'maga-loved',
    label: 'Approved By Patriots',
    description: 'MAGA tribe rep crossed +50.',
    predicate: () => usePower.getState().tribeRep.maga >= 50,
  },
  {
    id: 'left-loved',
    label: 'Approved By The Resistance',
    description: 'Left tribe rep crossed +50.',
    predicate: () => usePower.getState().tribeRep.left >= 50,
  },
  {
    id: 'speedrun-whale',
    label: 'Whale Speedrun',
    description: 'Reached Whale tier within 1 hour of session start.',
    predicate: () => false, // requires session-start tracking; Phase 5 polish
  },
  {
    id: 'tycoon',
    label: 'Tycoon',
    description: 'Net worth crossed $10M. The IRS is interested.',
    predicate: () => useMeta.getState().highestTierRankReached >= 3,
  },
  {
    id: 'crypto-loved',
    label: 'WAGMI Energy',
    description: 'Crypto tribe rep crossed +50.',
    predicate: () => usePower.getState().tribeRep.crypto >= 50,
  },
  {
    id: 'wsb-king',
    label: 'King of r/WSB',
    description: 'r/wsb tribe rep crossed +75. you are the regard others look up to.',
    predicate: () => usePower.getState().tribeRep.wsb >= 75,
  },
  {
    id: 'press-darling',
    label: 'Press Darling',
    description: 'Press tribe rep crossed +50. journalists actually like you.',
    predicate: () => usePower.getState().tribeRep.press >= 50,
  },
  {
    id: 'mom-disappointed',
    label: 'Mom Is Disappointed',
    description: "You let mom's relationship dip below 0.",
    predicate: () => useLife.getState().relationships.mom < 0,
  },
  {
    id: 'lawyer-retainer',
    label: 'Lawyer On Retainer',
    description: "You hired Markowitz. \$1,400/h forever.",
    predicate: () => useLife.getState().lawyerRetainerActiveSince !== null,
  },
  {
    id: 'no-life',
    label: 'No Life',
    description: '7-day login streak. Are you okay?',
    predicate: () => useMeta.getState().loginStreak >= 7,
  },
  {
    id: 'collected',
    label: 'Lore Collector',
    description: 'Discovered 10+ storylines.',
    predicate: () => Object.keys(useMeta.getState().loreCodex).length >= 10,
  },
  {
    id: 'first-ascend',
    label: 'Reborn',
    description: 'First voluntary ascension.',
    predicate: () => useMeta.getState().ascensions >= 1,
  },
  {
    id: 'three-runs',
    label: 'Repeat Offender',
    description: '3+ ascensions. You came back. We knew you would.',
    predicate: () => useMeta.getState().ascensions >= 3,
  },
  {
    id: 'true-death',
    label: 'The Irrecoverable',
    description: 'Triggered the true-death scene. There is no carrying over from this one.',
    predicate: () => useMeta.getState().graveyard.some((g) => g.causeOfDeath.includes('irrecoverable')),
  },
  {
    id: 'fbi-raided',
    label: 'Door Down',
    description: 'FBI raided your apartment.',
    predicate: () => useLife.getState().fbiHeat >= 75,
  },
  {
    id: 'hospital',
    label: 'Hospitalized',
    description: 'You collapsed at your desk. The doctor said: "you have to sleep at some point."',
    predicate: () => useLife.getState().health <= 20,
  },
  {
    id: 'sec-investigated',
    label: 'Under Investigation',
    description: 'SEC formally investigated you.',
    predicate: () => useLife.getState().secHeat >= 60,
  },
  {
    id: 'wife-came-back',
    label: 'Reconciled',
    description: 'Wife rep recovered above +50 after dipping below 0.',
    predicate: () => useLife.getState().relationships.wife >= 50,
  },
  {
    id: 'friends-in-high-places',
    label: 'Friends In High Places',
    description: '8+ Phone contacts. People know you now.',
    predicate: () => Object.keys(usePhone.getState().contacts).length >= 8,
  },
  {
    id: 'streak-30',
    label: 'Locked In',
    description: '30-day login streak. We do not endorse this.',
    predicate: () => useMeta.getState().loginStreak >= 30,
  },
  {
    id: 'all-themes',
    label: 'Color Theory',
    description: 'Reach Whale tier AND ascend twice.',
    predicate: () => useMeta.getState().highestTierRankReached >= 2 && useMeta.getState().ascensions >= 2,
  },
  {
    id: 'gala-host',
    label: 'Tax-Deductible Schmoozer',
    description: 'Hosted the Charity Gala. The "art" deduction works wonders.',
    predicate: () => useMeta.getState().loreCodex['arc-charity-gala'] != null,
  },
  {
    id: 'tiktok-debut',
    label: 'Vertical Genius',
    description: 'Did the TikTok thing. Recovery is ongoing.',
    predicate: () => useMeta.getState().loreCodex['arc-tiktok-debut'] != null,
  },
  {
    id: 'hostile-takeover',
    label: 'Board Room Brawler',
    description: 'Launched a hostile takeover.',
    predicate: () => useMeta.getState().loreCodex['arc-hostile-takeover'] != null,
  },
  {
    id: 'cult-founder',
    label: 'Founder',
    description: 'Started a "spiritual movement." Tax-exempt eligible. Members aware.',
    predicate: () => useMeta.getState().loreCodex['arc-cult-founding'] != null,
  },
  {
    id: 'rogan-on-air',
    label: 'On The Mic',
    description: 'Did Joe Rogan. The clip is forever.',
    predicate: () => useMeta.getState().loreCodex['arc-podcast-appearance'] != null,
  },
  {
    id: 'yacht-life',
    label: 'International Waters',
    description: '72 hours on a 240-foot yacht. Nobody asked questions. Hopefully.',
    predicate: () => useMeta.getState().loreCodex['arc-yacht-trip'] != null,
  },
  {
    id: 'rally-stand-in',
    label: 'Stand-In',
    description: 'Cameo at a rally. The Senator owes you. The Senator forgets debts.',
    predicate: () => useMeta.getState().loreCodex['arc-rally-stand-in'] != null,
  },
  {
    id: 'pump-coordinator',
    label: 'Pump Coordinator',
    description: 'Ran a coordinated $JEET pump. The SEC noticed.',
    predicate: () => useMeta.getState().loreCodex['arc-crypto-pump'] != null,
  },
  {
    id: 'court-survivor',
    label: 'Survived The Court',
    description: 'Took the SEC to trial.',
    predicate: () => useMeta.getState().loreCodex['arc-court-hearing'] != null,
  },
  {
    id: 'senate-bid',
    label: 'On The Ballot',
    description: 'Ran for Senate. Democracy got weirder.',
    predicate: () => useMeta.getState().loreCodex['arc-run-for-office'] != null,
  },
  {
    id: 'centibillionaire',
    label: 'Centibillionaire',
    description: 'Cash >= $100B. The economic equivalent of a small moon.',
    predicate: () => useMarkets.getState().cash >= 100_000_000_000,
  },
  {
    id: 'trillionaire',
    label: 'Trillionaire',
    description: 'Cash >= $1T. You alone are a sovereign wealth fund.',
    predicate: () => useMarkets.getState().cash >= 1_000_000_000_000,
  },
  {
    id: 'god-tier',
    label: 'God Tier',
    description: 'Cash >= $10T. Numbers stop meaning things.',
    predicate: () => useMarkets.getState().cash >= 10_000_000_000_000,
  },
  {
    id: 'broke-again',
    label: 'Broke Again',
    description: 'Cash dropped below $0 after having reached Tycoon tier. You earned this.',
    predicate: () => useMarkets.getState().cash < 0 && useMeta.getState().highestTierRankReached >= 3,
  },
  {
    id: 'irs-pacified',
    label: 'IRS Pacified',
    description: 'IRS heat reduced from 50+ to under 10. Lawyers earned every dollar.',
    predicate: () => useLife.getState().irsHeat < 10 && useMeta.getState().loreCodex['arc-irs-audit'] != null,
  },
  {
    id: 'all-acquisitions',
    label: 'Full Trinity',
    description: 'Owns the foundation, yacht, and jet. You are now functionally a sovereign.',
    predicate: () => {
      const w = useWallet.getState();
      return w.ownsCharityFoundation && w.ownsYacht && w.ownsJet;
    },
  },
  {
    id: 'maxed-tribe',
    label: 'Tribal Champion',
    description: 'Any single tribe rep crossed +80. They love you.',
    predicate: () => Object.values(usePower.getState().tribeRep).some((v) => v >= 80),
  },
  {
    id: 'hated-tribe',
    label: 'Persona Non Grata',
    description: 'Any single tribe rep crossed -80. They hate you.',
    predicate: () => Object.values(usePower.getState().tribeRep).some((v) => v <= -80),
  },
  {
    id: 'trump-shoutout',
    label: 'Truthed About',
    description: 'The President posted about you on TruthSocial-clone. Comments are 50/50.',
    predicate: () => useMeta.getState().loreCodex['arc-trump-shoutout'] != null,
  },
  {
    id: 'the-affair',
    label: 'The Affair',
    description: 'Met Sloane at the Carlyle.',
    predicate: () => useMeta.getState().loreCodex['arc-affair'] != null,
  },
  {
    id: 'mom-call',
    label: 'She Called',
    description: 'Mom called about Dad. The market was open. You picked up.',
    predicate: () => useMeta.getState().loreCodex['arc-mom-emergency'] != null,
  },
  {
    id: 'fed-day',
    label: 'Traded JPow',
    description: 'Sat through 4 minutes of "data-dependent" and made decisions.',
    predicate: () => useMeta.getState().loreCodex['arc-fed-decision'] != null,
  },
  {
    id: 'earnings-call',
    label: 'Beat On Guidance',
    description: 'Hosted an earnings call. Did not consult the deck. Tape moved.',
    predicate: () => useMeta.getState().loreCodex['arc-earnings-call'] != null,
  },
  {
    id: 'tax-season',
    label: 'Filed',
    description: 'Sat through 4 tax-strategy decisions. The IRS now has a folder.',
    predicate: () => useMeta.getState().loreCodex['arc-tax-season'] != null,
  },
  {
    id: 'bot-followers',
    label: 'Flattering Ratio',
    description: 'A New Yorker reporter audited your followers. They were 87% bots. You owned it.',
    predicate: () => useMeta.getState().loreCodex['arc-bot-followers'] != null,
  },
  {
    id: 'dog-returns',
    label: 'He Came Back',
    description: 'Bartholomew found his way home. You did not deserve him.',
    predicate: () => useMeta.getState().loreCodex['arc-dog-returns'] != null,
  },
  {
    id: 'pardon',
    label: 'Bought The Pardon',
    description: 'Lobbied for a presidential pardon.',
    predicate: () => useMeta.getState().loreCodex['arc-pardon'] != null,
  },
  {
    id: 'foreign-asset',
    label: 'Activated',
    description: '[unknown number] called in their favors. You answered.',
    predicate: () => useMeta.getState().loreCodex['arc-foreign-asset'] != null,
  },
  {
    id: 'bloomberg-interview',
    label: 'Sat For Bloomberg',
    description: '14 minutes, 4 hostile questions. You answered live.',
    predicate: () => useMeta.getState().loreCodex['arc-bloomberg-interview'] != null,
  },
  {
    id: 'risk-manager',
    label: 'Risk Manager',
    description: 'Placed your first stop or trailing-stop order. The pros do this. Allegedly.',
    predicate: () => useMarkets.getState().openOrders.some((o) => o.type === 'stop' || o.type === 'trail'),
  },
  {
    id: 'paper-hands',
    label: 'Paper Hands',
    description: 'Closed a position within 60 seconds of opening it. Conviction: zero.',
    predicate: () => {
      const fills = useMarkets.getState().recentFills;
      // Look for buy + sell of the same ticker within 60s
      for (let i = 0; i < fills.length; i++) {
        const a = fills[i]!;
        if (a.side !== 'sell') continue;
        for (let j = i + 1; j < fills.length; j++) {
          const b = fills[j]!;
          if (b.ticker === a.ticker && b.side === 'buy' && a.at - b.at < 60_000 && a.at - b.at > 0) return true;
        }
      }
      return false;
    },
  },
  {
    id: 'concentrated',
    label: 'All In',
    description: 'Held a single position worth >75% of your net worth. Diversification is for cowards.',
    predicate: () => {
      const m = useMarkets.getState();
      const now = Date.now();
      let posVal = 0;
      const positionVals: number[] = [];
      for (const p of m.positions) {
        const cfg = getTicker(p.ticker);
        const pr = cfg ? priceAt(cfg, now) : p.avgPrice;
        const v = pr * p.qty;
        positionVals.push(v);
        posVal += v;
      }
      const nw = m.cash + posVal;
      if (nw <= 0) return false;
      return positionVals.some((v) => v / nw > 0.75);
    },
  },
  {
    id: 'whale-club',
    label: 'Whale Club',
    description: 'Net worth crossed $100K. Welcome to the club. The club is mostly debt.',
    predicate: () => useMeta.getState().highestTierRankReached >= 2,
  },
  {
    id: 'tycoon-club',
    label: 'Tycoon Club',
    description: 'Net worth crossed $10M. You now have a wealth manager. He has a wealth manager.',
    predicate: () => useMeta.getState().highestTierRankReached >= 3,
  },
  {
    id: 'billionaire-club',
    label: 'Billionaire',
    description: 'Net worth crossed \$1B. You now exist as a paragraph in someone\'s phd thesis.',
    predicate: () => useMeta.getState().highestTierRankReached >= 4,
  },
  {
    id: 'sec-veteran',
    label: 'Known to the SEC',
    description: 'SEC heat crossed 50. They have a folder. The folder has tabs.',
    predicate: () => useLife.getState().secHeat >= 50,
  },
  {
    id: 'fbi-veteran',
    label: 'On a List',
    description: 'FBI heat crossed 50. The list is, allegedly, alphabetical.',
    predicate: () => useLife.getState().fbiHeat >= 50,
  },
  {
    id: 'irs-veteran',
    label: 'Schedule C, Subsection You',
    description: 'IRS heat crossed 50. You are a case number.',
    predicate: () => useLife.getState().irsHeat >= 50,
  },
  {
    id: 'stay-married',
    label: 'Stayed Married',
    description: 'Wife rep above 50. Improbable. Honored.',
    predicate: () => useLife.getState().relationships.wife >= 50,
  },
  {
    id: 'wife-hates-you',
    label: 'Wife Hates You',
    description: 'Wife rep at 0 or below. The dog has chosen sides.',
    predicate: () => useLife.getState().relationships.wife <= 0,
  },
  {
    id: 'mom-loves-you-extra',
    label: "Mom\'s Pride",
    description: 'Mom rep above 80. She\'s telling everyone at bridge.',
    predicate: () => useLife.getState().relationships.mom >= 80,
  },
  {
    id: 'lawyer-broke',
    label: 'Bills Paid',
    description: 'Spent \$50K+ on lawyers (cumulative). They will not, however, send a card.',
    predicate: () => {
      // Crude: lawyer rep above 30 implies meaningful billable history. Best-effort.
      return useLife.getState().relationships.lawyer >= 30;
    },
  },
  {
    id: 'tweet-king',
    label: 'Y Influencer',
    description: 'Followers crossed 1,000. The replies are, statistically, mostly bots.',
    predicate: () => {
      try { return useXClone.getState().followers >= 1000; } catch { return false; }
    },
  },
  {
    id: 'tweet-mogul',
    label: 'Y Mogul',
    description: 'Followers crossed 100,000. You are now, technically, a public figure.',
    predicate: () => {
      try { return useXClone.getState().followers >= 100_000; } catch { return false; }
    },
  },
  {
    id: 'first-100k-trade',
    label: 'Six Figures Realized',
    description: 'Realized P&L crossed +\$100K. Wife will, later, ask where it went.',
    predicate: () => useMarkets.getState().realizedPnl >= 100_000,
  },
  {
    id: 'down-bad',
    label: 'Down Bad',
    description: 'Realized P&L crossed -\$50K. The dog noticed.',
    predicate: () => useMarkets.getState().realizedPnl <= -50_000,
  },
  {
    id: 'first-1m-trade',
    label: 'Seven Figures Realized',
    description: 'Realized P&L crossed +\$1M. You are, now, the kind of person who is "in markets."',
    predicate: () => useMarkets.getState().realizedPnl >= 1_000_000,
  },
  {
    id: 'big-pharma',
    label: 'Big Pharma Holder',
    description: 'Held a $PHRM position worth >\$50K. Insurance status pending.',
    predicate: () => {
      const p = useMarkets.getState().positions.find(x => x.ticker === 'PHRM');
      return !!p && p.qty * p.avgPrice >= 50_000;
    },
  },
  {
    id: 'crypto-degen',
    label: 'Memecoin Believer',
    description: 'Held a position in $JEET, $RUGS, or $PEPE7. We will not judge. (We are judging.)',
    predicate: () => {
      const set = new Set(['JEET', 'RUGS', 'PEPE7']);
      return useMarkets.getState().positions.some((p) => set.has(p.ticker));
    },
  },
  {
    id: 'all-themes',
    label: 'Aesthete',
    description: 'Unlocked all themes. You\'ve seen yourself in every color.',
    predicate: () => {
      const meta = useMeta.getState();
      // Aesthete = Tycoon-tier (rank 3) AND ≥ 2 ascensions (covers Cyberpunk + WSB chaos + Gold)
      return meta.highestTierRankReached >= 3 && meta.ascensions >= 2;
    },
  },
  {
    id: 'no-sleep',
    label: 'No Sleep',
    description: 'Sanity dropped to 10 or below. The chart is, somehow, still up.',
    predicate: () => useLife.getState().sanity <= 10,
  },
  {
    id: 'broke-broke',
    label: "Actually Broke",
    description: 'Cash dropped to 0 or below. There\'s a desperation tab in the wallet for a reason.',
    predicate: () => useMarkets.getState().cash <= 0,
  },
  {
    id: 'every-tribe',
    label: 'Universally Liked',
    description: 'Above-zero rep with all 8 tribes simultaneously. Statistically unusual.',
    predicate: () => {
      const tr = usePower.getState().tribeRep;
      return Object.values(tr).every((v) => v > 0);
    },
  },
  {
    id: 'every-tribe-hated',
    label: 'Universally Disliked',
    description: 'Below-zero rep with all 8 tribes simultaneously. A rare achievement. Also a rare condition.',
    predicate: () => {
      const tr = usePower.getState().tribeRep;
      return Object.values(tr).every((v) => v < 0);
    },
  },
  {
    id: 'lawyer-loved',
    label: 'Lou\'s Favorite',
    description: 'Lawyer rep above 60. He has, allegedly, mentioned you to his wife. By name.',
    predicate: () => useLife.getState().relationships.lawyer >= 60,
  },
  {
    id: 'best-friend-bro',
    label: 'BFF',
    description: 'Best friend rep above 80. He drove over at 2am at least once.',
    predicate: () => useLife.getState().relationships.best_friend >= 80,
  },
  {
    id: 'sec-resolved',
    label: 'Settled',
    description: 'SEC heat cut in half from a peak above 50. The lawyer will, somehow, send a card.',
    predicate: () => {
      const h = useLife.getState().secHeat;
      return h <= 25 && useMeta.getState().isAchievementUnlocked('sec-veteran');
    },
  },
  {
    id: 'jet-set',
    label: 'Jet-Set',
    description: 'Owned the jet AND the yacht. The wife uses both more than you.',
    predicate: () => {
      const w = useWallet.getState();
      return w.ownsJet && w.ownsYacht;
    },
  },
];

export function checkAchievements(): void {
  const meta = useMeta.getState();
  for (const a of ACHIEVEMENTS) {
    if (meta.isAchievementUnlocked(a.id)) continue;
    let unlocked = false;
    try { unlocked = a.predicate(); } catch { unlocked = false; }
    if (!unlocked) continue;
    meta.unlockAchievement(a.id);
    useNotifications.getState().push({
      surfaceId: 'tv',
      severity: 'important',
      text: `🏆 ${a.label}`,
    });
    sounds.achievementUnlock();
  }
}
